import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_PICKER_EVENT, StorePerformanceInterval, StorePerformanceIntervalMap } from '../../constants';
import { PickerType } from '../../global-state/types';
import useEventTracker from '../../hooks/useEventTracker';
import { DatePicker } from '../../stories/molecules/date-picker/DatePicker';
import { homeTranslations } from '../../translations';

type HomeDatePickerProps = {
  currentDate: Date;
  dateLabel: string;
  onDateChange: (date: Date, interval: StorePerformanceInterval) => void;
  onRangeChange?: (dates: [Date, Date], interval: StorePerformanceInterval) => void;
  showMonthyPicker?: boolean;
  showWeeklyPicker?: boolean;
  showDateRangePicker?: boolean;
  isDatePickerDisabled?: boolean;
};

export const HomeDatePicker: FC<HomeDatePickerProps> = ({
  currentDate,
  dateLabel,
  onDateChange,
  onRangeChange,
  showMonthyPicker,
  showWeeklyPicker = true,
  isDatePickerDisabled = false,
  showDateRangePicker,
}) => {
  const { t } = useTranslation();
  const { datePicker } = homeTranslations;
  const trackEvent = useEventTracker();
  const [pickerType, setPickerType] = useState<PickerType>('date');

  const handleOnChange = (date: Date) => {
    onDateChange(date, StorePerformanceIntervalMap[pickerType]);
    trackEvent(DATE_PICKER_EVENT, 'click', [
      { name: 'Page', stringValue: 'home' },
      { name: 'SelectedDate', stringValue: date.toISOString() },
      { name: 'PickerType', stringValue: pickerType },
    ]);
  };

  const handleOnRangeChange = (dates: [Date, Date]) => {
    if (onRangeChange) {
      onRangeChange(dates, StorePerformanceIntervalMap[pickerType]);
    }
    trackEvent(DATE_PICKER_EVENT, 'click', [
      { name: 'Page', stringValue: 'home' },
      { name: 'SelectedStartDate', stringValue: dates[0].toISOString() },
      { name: 'SelectedEndDate', stringValue: dates[1].toISOString() },
      { name: 'PickerType', stringValue: pickerType },
    ]);
  };

  return (
    <DatePicker
      inputSelectedDate={currentDate}
      onChange={(date) => {
        handleOnChange(date);
      }}
      disableDatePicker={isDatePickerDisabled}
      onRangeChange={handleOnRangeChange}
      label={dateLabel}
      pickerType={pickerType}
      showCalendarIcon
      views={pickerType === 'month' ? ['year', 'month'] : ['day']}
      actionBar={
        <RadioGroup
          aria-labelledby="date-picker-radio-group"
          defaultValue={pickerType}
          onChange={(event) => {
            setPickerType(event.target.value as PickerType);
          }}
          sx={{ display: 'flex', py: 4, px: 4 }}
        >
          <FormControlLabel
            value="date"
            control={<Radio />}
            label={t(datePicker.radioLabel1.key, datePicker.radioLabel1.defaultValue)}
          />
          {showWeeklyPicker && (
            <FormControlLabel
              value="week"
              control={<Radio />}
              label={t(datePicker.radioLabel2.key, datePicker.radioLabel2.defaultValue)}
            />
          )}
          {showMonthyPicker && (
            <FormControlLabel
              value="month"
              control={<Radio />}
              label={t(datePicker.radioLabel3.key, datePicker.radioLabel3.defaultValue)}
            />
          )}
          {showDateRangePicker && (
            <FormControlLabel
              value="range"
              control={<Radio />}
              label={t(datePicker.radioLabel4.key, datePicker.radioLabel4.defaultValue)}
            />
          )}
        </RadioGroup>
      }
    />
  );
};
